import { colors, typography } from '@mehilainen/mds-tokens';
const { baseTypography } = typography;
//Common styles for all MUI input fields
export const getThemeFormElements = (tokens) => {
    var _a, _b;
    const useStandardLabel = tokens.values.inputLabelPlacement === 'standard';
    const inputTokens = tokens.uiElementInputFields;
    return {
        MuiFormControl: {
            defaultProps: {
                variant: 'outlined',
                size: 'small',
                className: useStandardLabel ? 'MdsTextField' : '',
            },
            styleOverrides: {
                root: ({ ownerState }) => {
                    var _a;
                    if (ownerState.variant !== 'outlined') {
                        return;
                    }
                    if (!useStandardLabel) {
                        return {
                            '& fieldset': {
                                paddingLeft: ((_a = inputTokens.common.paddingLeft) !== null && _a !== void 0 ? _a : 16) - 5 + 'px',
                                '& legend': {
                                    fontSize: baseTypography.size[200].rem,
                                    fontWeight: baseTypography.weight.Regular,
                                    lineHeight: baseTypography.height.Small,
                                },
                            },
                        };
                    }
                    return {
                        // NOTE: MUI Textfield does not allow variants so this is a custom variant via class
                        '&.MdsTextField': {
                            '& > label': {
                                position: 'unset',
                                paddingBottom: '4px',
                                WebkitTransform: 'unset',
                                MozTransform: 'unset',
                                msTransform: 'unset',
                                transform: 'unset',
                                fontSize: ownerState.size === 'small'
                                    ? inputTokens.sizeSmall.labelFontSize
                                    : inputTokens.sizeMedium.labelFontSize,
                                fontWeight: ownerState.size === 'small'
                                    ? inputTokens.sizeSmall.labelFontWeight
                                    : inputTokens.sizeMedium.assistiveTextWeight,
                                lineHeight: baseTypography.height.Medium,
                            },
                            '& > [class*="MuiFormHelperText-root"]': {
                                // Outlined helpertext has default margin
                                marginLeft: 0,
                            },
                            '& legend': {
                                // Prevent notch
                                maxWidth: '0.01px !important',
                            },
                        },
                    };
                },
            },
        },
        MuiTextField: {
            defaultProps: {
                variant: 'outlined',
                size: 'small',
                minRows: 3,
                maxRows: 8,
                // NOTE: This must be set manually to ' ' if standard outlined notched variant is used
                className: useStandardLabel ? 'MdsTextField' : '',
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: ({ ownerState }) => {
                    if (useStandardLabel) {
                        return;
                    }
                    return {
                        fontSize: ownerState.size === 'small'
                            ? inputTokens.sizeSmall.labelFontSize
                            : inputTokens.sizeMedium.labelFontSize,
                        fontWeight: ownerState.size === 'small'
                            ? inputTokens.sizeSmall.labelFontWeight
                            : inputTokens.sizeMedium.assistiveTextWeight,
                        lineHeight: baseTypography.height.Medium,
                        transform: `translate(${inputTokens.common.paddingLeft}px, -7px) scale(1)`,
                        '&:not([class*="MuiInputLabel-shrink"])': {
                            fontSize: baseTypography.size[300].rem,
                            transform: ownerState.size === 'small'
                                ? `translate(${inputTokens.common.paddingLeft}px, 10px) scale(1)`
                                : `translate(${inputTokens.common.paddingLeft}px, 14px) scale(1)`,
                        },
                    };
                },
            },
        },
        MuiFormLabel: {
            styleOverrides: {
                filled: {
                    color: colors[(_a = inputTokens.common.labelFilledColor) !== null && _a !== void 0 ? _a : 'Gray500'],
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                input: {
                    '&::placeholder': {
                        opacity: 1,
                        color: colors.Gray600,
                    },
                },
                root: ({ ownerState }) => {
                    if (ownerState.multiline) {
                        return;
                    }
                    return {
                        minHeight: ownerState.size === 'small'
                            ? inputTokens.sizeSmall.height
                            : inputTokens.sizeMedium.height,
                        // For some reason error- css selector was not working
                        '&.Mui-error': {
                            '& > [class*="MuiSvgIcon-root"]': {
                                color: colors.Error500,
                            },
                        },
                    };
                },
            },
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: ({ ownerState, theme }) => {
                    const color = !ownerState.error && ownerState.focused
                        ? { color: theme.palette.primary.main }
                        : {};
                    return Object.assign({ fontSize: ownerState.size === 'small'
                            ? inputTokens.sizeSmall.assistiveTextSize
                            : inputTokens.sizeMedium.assistiveTextSize, fontWeight: ownerState.size === 'small'
                            ? inputTokens.sizeSmall.assistiveTextWeight
                            : inputTokens.sizeMedium.assistiveTextWeight, lineHeight: baseTypography.height.Medium, marginLeft: 0, marginTop: '4px' }, color);
                },
            },
        },
        MuiInput: {
            styleOverrides: {
                root: {
                    '&:hover:not([class*="Mui-disabled"]):before': {
                        borderColor: colors.Primary500,
                    },
                    '&::before': {
                        borderColor: colors[(_b = inputTokens.common.borderColor) !== null && _b !== void 0 ? _b : 'Gray500'],
                    },
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: ({ ownerState }) => {
                    var _a;
                    return {
                        fontSize: ownerState.size === 'small'
                            ? inputTokens.sizeSmall.contentFontSize
                            : inputTokens.sizeMedium.contentFontSize,
                        lineHeight: baseTypography.height.Small,
                        backgroundColor: colors.White,
                        color: colors.Gray900,
                        borderColor: colors[(_a = inputTokens.common.borderColor) !== null && _a !== void 0 ? _a : 'Gray500'],
                        borderRadius: inputTokens.common.borderRadius,
                        // textarea stylings
                        '&[class*="MuiInputBase-multiline"]': {
                            padding: '0 16px 0 0',
                            '& [class*="MuiInputBase-input"]': {
                                paddingRight: 0,
                            },
                        },
                        '&[class*="Mui-error"]': {
                            borderColor: colors.Error500,
                            color: colors.Error500,
                        },
                        '&:hover:not([class*="Mui-error"]):not([class*="Mui-disabled"])': {
                            borderColor: colors.Primary500,
                        },
                        '&[class*="Mui-disabled"]': {
                            borderColor: colors.Gray300,
                        },
                    };
                },
                // NOTE: No idea why this exists in dom if notched is false, but this actually contains all the needed border rules
                notchedOutline: {
                    borderColor: 'inherit !important',
                    borderWidth: '1px !important', // Active input has width 2 px on default
                },
                input: {
                    padding: `8px ${inputTokens.common.paddingLeft}px`,
                },
            },
        },
        MuiInputAdornment: {
            styleOverrides: {
                root: {
                    color: colors.Gray600,
                },
            },
        },
    };
};
